import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import { GetGroup, UpdateGroup } from '../../api/groups-endpoint';
import SnackbarAlert from '../../components/Alerts';
import CancelButton from '../../components/CancelButton';
import GroupDetails from '../../components/groups/GroupDetails';
import useConfigStyles from '../../components/useConfigStyles';
import SubmitButton from '../../components/SubmitButton';
import { getLinkToGroup } from '../../navigator/links';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGroupsContext } from '../../stores/groups-context';
import { Group } from '../../types/group';
import { defaultGroup } from '../../types/defaultValues';

function GroupEditPage(): JSX.Element {
  const { groupName } = useParams<{ groupName: string }>();
  const [group, setGroup] = useState<Group>(defaultGroup);
  const [fetched, setFetched] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [notify, toggleNotification] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const classes = useConfigStyles();
  const navigate = useNavigate();
  const groupsContext = useGroupsContext();

  const fetchGroup = useCallback(async () => {
    if (groupName) {
      const response = await GetGroup(groupName);
      if (response.success && response.data) {
        setGroup(response.data);
      } else {
        setResponseMessage(response.message || '');
      }
      setFetched(response.success);
    }
  }, [groupName]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const response = await UpdateGroup(groupName!, group);
    toggleNotification(true);
    if (response.success) {
      setResponseMessage('Success');
      groupsContext.fetchData();
      navigate(getLinkToGroup(group.Name));
    } else {
      setResponseMessage(response.message || '');
      setErrors(response.errors || {});
    }
  };

  const handleCloseSnackbar = () => {
    toggleNotification(false);
  };

  const handleCancelClick = () => {
    navigate(getLinkToGroup(groupName!));
  };

  const cleanErrors = (name: string) => {
    if (errors[name]) {
      delete errors[name];
    }
  };

  const handleGroupUpdate = (updatedGroup: Group, fieldName?: string) => {
    setGroup(updatedGroup);
    if (fieldName) {
      cleanErrors(fieldName);
    }
  };

  return !fetched ? (
    <span>{responseMessage}</span>
  ) : (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Card>
        <CardContent>
          <GroupDetails group={group} editable errors={errors} onGroupUpdate={handleGroupUpdate} />
        </CardContent>
        <CardActions>
          <Grid container alignItems='center' justifyContent='center' direction='row' spacing={5}>
            <Grid item>
              <CancelButton label='Cancel' className={classes.button} onClick={handleCancelClick} />
            </Grid>
            <Grid item>
              <SubmitButton className={classes.button} label='Update Group' />
            </Grid>
          </Grid>
        </CardActions>
      </Card>
      <SnackbarAlert open={notify} message={responseMessage} handleClose={handleCloseSnackbar} />
    </form>
  );
}

export default GroupEditPage;
